import classNames from "classnames";
import { Fragment, useEffect, useState } from "react";
import { isOpenToday } from "@/lib/openingHours";

const weekdayLabels: Record<Travelguide.Weekday, string> = {
  monday: "Montag",
  tuesday: "Dienstag",
  wednesday: "Mittwoch",
  thursday: "Donnerstag",
  friday: "Freitag",
  saturday: "Samstag",
  sunday: "Sonntag",
};

const schemaDays: Record<Travelguide.Weekday, string> = {
  monday: "https://schema.org/Monday",
  tuesday: "https://schema.org/Tuesday",
  wednesday: "https://schema.org/Wednesday",
  thursday: "https://schema.org/Thursday",
  friday: "https://schema.org/Friday",
  saturday: "https://schema.org/Saturday",
  sunday: "https://schema.org/Sunday",
};

const Specification = ({
  day,
  from,
  to,
}: { day: Travelguide.Weekday } & Travelguide.OpeningHoursRange) => (
  <span
    itemProp="openingHoursSpecification"
    itemScope
    itemType="https://schema.org/OpeningHoursSpecification"
  >
    <meta itemProp="dayOfWeek" content={schemaDays[day]} />
    <meta itemProp="opens" content={from} />
    <meta itemProp="closes" content={to} />
    {from}–{to} Uhr
  </span>
);

type OpeningHoursProps = {
  openingHours: Array<Travelguide.POISection.OpeningHours>;
  specialOpeningHours?: Array<Travelguide.POISection.OpeningHoursException>;
};

const Status = ({ now, ...section }: OpeningHoursProps & { now?: Date }) => {
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const { openingHours, specialOpeningHours } = section;

  useEffect(() => {
    setIsOpen(isOpenToday({ openingHours, specialOpeningHours }, now));
  }, [openingHours, specialOpeningHours, now]);

  if (isOpen === null) {
    return null;
  }

  return (
    <div className="flex flex-row items-center gap-2 max-w-fit rounded bg-primary/5 px-2 py-1">
      <span
        aria-hidden
        className={classNames(
          "h-2 w-2 rounded-full",
          isOpen ? "bg-green" : "bg-accent"
        )}
      />
      {isOpen ? "Heute geöffnet" : "Heute geschlossen"}
    </div>
  );
};

export const OpeningHours = ({
  openingHours,
  specialOpeningHours,
  now,
}: OpeningHoursProps & {
  now?: Date;
}) => (
  <>
    <Status
      openingHours={openingHours}
      specialOpeningHours={specialOpeningHours}
      now={now}
    />
    <dl className="grid grid-cols-[max-content,max-content] gap-x-6 text-list">
      {openingHours.map(({ day, hours }) => (
        <Fragment key={day}>
          <dt className="whitespace-nowrap">{weekdayLabels[day]}</dt>
          <dd className="flex flex-col">
            {hours.length ? (
              hours.map((range, i) => (
                <Specification key={i} day={day} {...range} />
              ))
            ) : (
              <span>geschlossen</span>
            )}
          </dd>
        </Fragment>
      ))}
    </dl>
  </>
);
